import {
  Box,
  Paper,
  useMediaQuery,
  useTheme,
  Typography,
  CircularProgress,
  Grid,
} from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import React from "react";

import { fetchRecordedContactsInsights } from "../../store/RecordedContactInsights/recordedContactsInsightsThunks";
import SummaryCards from "./Components/SummaryCards";
import CompetencyReviewDatagrid from "./Components/CompetencyReviewDatagrid";
import PlaylistDatagrid from "./Components/PlaylistDatagrid";
import PageTitle from "../../components/PageTitle";
import FilterToolbar from "./Components/FilterToolbar";
import PieGraph from "./Components/Dashboard/PieGraph";
import BarGraph from "./Components/Dashboard/BarGraph";

import "./Components/Dashboard/PieGraph.css";

const COLORS = [
  "#4318FF",
  "#6AD2FF",
  "#E31A1A",
  "#1AD7E3",
  "#4318FF",
  "#39B8FF",
];

function RecordedContactInsights() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();

  const { data, loading, error } = useSelector(
    (state) => state.recordedContactsInsights
  );

  const [filters, setFilters] = useState({});

  useEffect(() => {
    dispatch(fetchRecordedContactsInsights(filters));
  }, [dispatch, filters]);

  const deviceDistributionData = useMemo(() => {
    if (!data?.contacts) return [];
    return data.contacts.reduce((acc, contact) => {
      const functionName = contact.function || "Unknown";
      const existing = acc.find((item) => item.name === functionName);
      if (existing) {
        existing.value += 1;
      } else {
        acc.push({
          name: functionName,
          value: 1,
          color: COLORS[acc.length % COLORS.length],
        });
      }
      return acc;
    }, []);
  }, [data?.contacts]);

  const stackedBarData = useMemo(() => {
    if (!data?.contacts) return [];
    return data.contacts.reduce((acc, contact) => {
      const functionName = contact.function || "Unknown";
      const areaName = contact.area || "Unknown";
      let regionEntry = acc.find((item) => item.name === functionName);
      if (!regionEntry) {
        regionEntry = { name: functionName };
        acc.push(regionEntry);
      }
      regionEntry[areaName] = (regionEntry[areaName] || 0) + 1;
      return acc;
    }, []);
  }, [data?.contacts]);

  const departments = useMemo(() => {
    if (!data?.contacts) return [];
    return [
      ...new Set(
        data.contacts
          .map((contact) => contact.area)
          .filter((area) => area && area !== "Unknown")
      ),
    ];
  }, [data?.contacts]);

  const commonBoxShadow = "0px 4px 20px rgba(0, 0, 0, 0.1)";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: "100vh",
        overflow: "hidden",
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          p: isSmallScreen ? 1 : 2.3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: commonBoxShadow,
          position: "relative",
          zIndex: 1,
        }}
      >
        <PageTitle
          title="Recorded Contact Insights"
          subtitle="Explore contact competency reviews and playlist activity"
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 3,
          height: "calc(100vh - 120px)",
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <FilterToolbar filters={filters} setFilters={setFilters} />
            </Box>
          </Grid>

          {loading ? (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "300px",
                }}
              >
                <CircularProgress />
              </Box>
            </Grid>
          ) : error ? (
            <Grid item xs={12}>
              <Typography color="error">{error}</Typography>
            </Grid>
          ) : (
            <>
              <Grid item xs={12}>
                <Box sx={{ boxShadow: "none" }}>
                  <SummaryCards data={data} />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: "400px",
                    backgroundColor: theme.palette.background.paper,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2,
                    boxShadow: commonBoxShadow,
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Contacts by Function
                  </Typography>
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <PieGraph data={deviceDistributionData} />
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={8}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: "400px",
                    backgroundColor: theme.palette.background.paper,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2,
                    boxShadow: commonBoxShadow,
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Department Distribution by Region
                  </Typography>
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <BarGraph data={stackedBarData} departments={departments} />
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 2,
                    boxShadow: commonBoxShadow,
                  }}
                >
                  <Box sx={{ p: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      Recorded Contacts Competency Status
                    </Typography>
                  </Box>
                  <CompetencyReviewDatagrid
                    rows={data?.contacts || []}
                    filters={filters}
                  />
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 2,
                    boxShadow: commonBoxShadow,
                  }}
                >
                  <Box sx={{ p: 1, marginTop: 7 }}>
                    <Typography variant="h6" gutterBottom>
                      Playlists Requiring Review
                    </Typography>
                  </Box>
                  <PlaylistDatagrid
                    rows={data?.playlists_requiring_review || []}
                  />
                </Paper>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </Box>
  );
}

export default RecordedContactInsights;
